import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';

const G = '#00C060';
const G_DARK = '#00A651';
const SIDEBAR_BG = '#0a1f0c';
const BORDER_SIDE = 'rgba(0, 192, 96, 0.4)';
const BG = '#f0f7f2';
const CARD = '#ffffff';
const BORDER = 'rgba(0, 192, 96, 0.4)';
const SHADOW = '0 2px 12px rgba(0,0,0,0.08)';

const getColor = (idx) => {
  const colors = ['#00A651', '#52c41a', '#faad14', '#1890ff', '#722ed1', '#eb2f96', '#13c2c2'];
  return colors[idx % colors.length];
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout} user={user} />
      <MainContent activeTab={activeTab} />
    </div>
  );
};

const Sidebar = ({ activeTab, setActiveTab, onLogout, user }) => {
  const navigate = useNavigate();
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-chart-line' },
    { id: 'sessions', label: 'Sessions', icon: 'fa-calendar-alt', external: true },
    { id: 'pdvs', label: 'Points de Vente', icon: 'fa-store', external: true },
    { id: 'stocks', label: 'Stocks', icon: 'fa-boxes', external: true },
    { id: 'daily-quotas', label: 'Quotas Journaliers', icon: 'fa-calendar-day', external: true },
    { id: 'prizes', label: 'Lots', icon: 'fa-gift', external: true },
    { id: 'injection', label: 'Injection', icon: 'fa-syringe', external: true },
    { id: 'participants', label: 'Participants', icon: 'fa-users', external: true },
    { id: 'users', label: 'Utilisateurs', icon: 'fa-user-cog', external: true },
  ];

  return (
    <div style={{ width: '260px', background: SIDEBAR_BG, minHeight: '100vh', display: 'flex', flexDirection: 'column', borderRight: `2px solid ${BORDER_SIDE}`, flexShrink: 0 }}>
      <div style={{ padding: '24px 20px', borderBottom: `1px solid ${BORDER_SIDE}`, textAlign: 'center', background: 'rgba(0,192,96,0.07)' }}>
        <img src="/logo-white.png" alt="Reaktor Nature Power" style={{ width: '110px', height: 'auto', filter: 'drop-shadow(0 4px 12px rgba(0,192,96,0.3))' }} />
        <p style={{ color: G, margin: '8px 0 0', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px' }}>Administration</p>
      </div>
      <div style={{ padding: '16px', flex: 1 }}>
        {menuItems.map(item => (
          <button key={item.id}
            onClick={() => item.external ? navigate(`/${item.id}`) : setActiveTab(item.id)}
            style={{
              width: '100%', padding: '12px 16px', border: 'none',
              background: activeTab === item.id ? `rgba(0,192,96,0.25)` : 'transparent',
              textAlign: 'left',
              color: activeTab === item.id ? '#ffffff' : 'rgba(255,255,255,0.75)',
              cursor: 'pointer', borderRadius: '10px', marginBottom: '4px',
              fontSize: '14px', fontWeight: activeTab === item.id ? 700 : 500,
              display: 'flex', alignItems: 'center', gap: '12px',
              borderLeft: activeTab === item.id ? `3px solid ${G}` : '3px solid transparent',
              transition: 'all 0.2s'
            }}>
            <i className={`fas ${item.icon}`} style={{ width: '18px', textAlign: 'center', color: activeTab === item.id ? G : 'rgba(255,255,255,0.6)' }}></i>
            {item.label}
          </button>
        ))}
        <button onClick={onLogout} style={{
          width: '100%', padding: '11px 16px', background: 'rgba(255,77,79,0.12)',
          color: '#ff6b6b', border: '1px solid rgba(255,77,79,0.5)', borderRadius: '10px',
          cursor: 'pointer', fontSize: '14px', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '12px'
        }}>
          <i className="fas fa-sign-out-alt"></i>Déconnexion
        </button>
      </div>
      <div style={{ padding: '16px', borderTop: `1px solid ${BORDER_SIDE}`, background: 'rgba(0,192,96,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '42px', height: '42px', background: `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a3322', fontSize: '18px', fontWeight: 700, boxShadow: `0 4px 12px rgba(0,192,96,0.4)` }}>
            {user?.username?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <div>
            <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '14px' }}>{user?.username || 'Admin'}</div>
            <div style={{ fontSize: '12px', color: G, fontWeight: '600' }}>{user?.role || ''}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainContent = ({ activeTab }) => {
  const [stats, setStats] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadStats = async () => {
    try {
      const dashRes = await api.get('/admin/stats/dashboard', { params: { brand: BRAND, strict_brand: true } });
      setStats(dashRes.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' });
    }
    try {
      const hourlyRes = await api.get('/admin/stats/hourly', { params: { brand: BRAND } });
      setHourlyData(hourlyRes.data);
    } catch (_) {}
  };

  useEffect(() => {
    if (activeTab === 'dashboard') loadStats();
  }, [activeTab]);

  if (activeTab !== 'dashboard') {
    return (
      <div style={{ flex: 1, background: BG, padding: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: CARD, borderRadius: '16px', padding: '80px 40px', textAlign: 'center', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
          <i className="fas fa-tools" style={{ fontSize: '64px', color: G, marginBottom: '24px', display: 'block', opacity: 0.5 }}></i>
          <h3 style={{ color: '#1a3322', marginBottom: '12px', fontSize: '22px', fontWeight: '700' }}>Module en développement</h3>
          <p style={{ color: 'rgba(0,0,0,0.45)', fontSize: '15px' }}>Ce module sera bientôt disponible.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, background: BG, padding: '32px', overflowY: 'auto' }}>
      {message.text && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-4`} role="alert">
          {message.text}
          <button type="button" className="btn-close btn-close-white" onClick={() => setMessage({ type: '', text: '' })}></button>
        </div>
      )}

      <div style={{ background: CARD, borderRadius: '16px', padding: '20px 28px', marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', background: `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 14px rgba(0,192,96,0.35)` }}>
            <i className="fas fa-bolt" style={{ color: '#1a3322', fontSize: '20px' }}></i>
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#1a3322' }}>Dashboard</h1>
            <p style={{ margin: 0, fontSize: '12px', color: '#00A651', fontWeight: '600' }}>Reaktor Nature Power</p>
          </div>
        </div>
        <button onClick={loadStats} className="btn btn-sm fw-bold"
          style={{ background: 'rgba(0,192,96,0.15)', border: `1px solid rgba(0,192,96,0.4)`, color: G, fontSize: '12px' }}>
          <i className="fas fa-sync-alt me-1"></i>Actualiser
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '28px' }}>
        <KPICard title="Participants" value={stats?.summary?.total_participations || 0} icon="fa-user-group" color="#1890ff" />
        <KPICard title="Gagnants" value={stats?.summary?.total_winners || 0} icon="fa-medal" color={G} />
        <KPICard title="Lots Gagnés" value={stats?.summary?.total_prizes || 0} icon="fa-gift" color="#faad14" />
        <KPICard title="Bouteilles" value={stats?.summary?.total_bottles || 0} icon="fa-bottle-water" color="#eb2f96" />
        <KPICard title="PDV Actifs" value={stats?.summary?.active_points || 0} icon="fa-store" color="#722ed1" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
        <div style={{ background: CARD, borderRadius: '16px', padding: '24px', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
          <h3 style={{ color: '#1a3322', fontSize: '16px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '32px', height: '32px', background: `rgba(0,192,96,0.15)`, borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-gift" style={{ color: G, fontSize: '14px' }}></i>
            </span>
            Répartition des Lots
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {stats?.prizes && Object.entries(stats.prizes).map(([prizeType, data], idx) => (
              <div key={prizeType} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.2)` }}>
                <span style={{ color: '#2a5a3a', fontSize: '14px', fontWeight: 600 }}>{prizeType}</span>
                <span style={{ color: getColor(idx), fontSize: '20px', fontWeight: 800 }}>{data.won}</span>
              </div>
            ))}
            {!stats?.prizes && <p style={{ color: 'rgba(0,0,0,0.35)', fontSize: '14px' }}>Aucune donnée</p>}
          </div>
        </div>

        <div style={{ background: CARD, borderRadius: '16px', padding: '24px', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
          <h3 style={{ color: '#1a3322', fontSize: '16px', fontWeight: '700', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '32px', height: '32px', background: 'rgba(24,144,255,0.15)', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-share-nodes" style={{ color: '#1890ff', fontSize: '14px' }}></i>
            </span>
            Mode de Participation
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { icon: 'fab fa-whatsapp', label: 'WhatsApp', value: stats?.engagement?.whatsapp || 0, rate: stats?.engagement?.whatsapp_rate || 0, color: '#25D366' },
              { icon: 'fas fa-mobile-screen', label: 'Direct', value: stats?.engagement?.direct || 0, rate: stats?.engagement?.direct_rate || 0, color: '#1890ff' },
              { icon: 'fas fa-qrcode', label: 'QR Code', value: stats?.engagement?.qr_code || 0, rate: stats?.engagement?.qr_code_rate || 0, color: '#8B5CF6' },
            ].map(m => (
              <div key={m.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(0,0,0,0.25)', borderRadius: '10px', border: `1px solid rgba(0,192,96,0.2)` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', background: m.color + '22', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={m.icon} style={{ color: m.color, fontSize: '16px' }}></i>
                  </div>
                  <div>
                    <span style={{ color: '#1a3322', fontSize: '14px', fontWeight: 700, display: 'block' }}>{m.label}</span>
                    <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: '12px' }}>{m.rate}% du total</span>
                  </div>
                </div>
                <span style={{ color: m.color, fontSize: '22px', fontWeight: 800 }}>{m.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CommercialPressureTable hourlyData={hourlyData} />

      <div style={{ background: CARD, borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, overflow: 'hidden', marginTop: '28px' }}>
        <div style={{ padding: '18px 24px', borderBottom: `1px solid rgba(0,192,96,0.2)`, background: 'rgba(0,0,0,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: '#1a3322', fontSize: '16px', fontWeight: '700', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '32px', height: '32px', background: 'rgba(250,173,20,0.15)', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-store" style={{ color: '#faad14', fontSize: '14px' }}></i>
            </span>
            Performance par Point de Vente
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #00C060 0%, #00A651 100%)' }}>
                {['Point de Vente', 'Ville', 'Inscrits', 'Gagnants', 'Bouteilles', 'Performance'].map(h => (
                  <th key={h} style={{ padding: '14px 12px', textAlign: h === 'Point de Vente' || h === 'Ville' ? 'left' : 'center', color: '#fff', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats?.by_pos?.map((pos, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid rgba(0,192,96,0.15)`, background: idx % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)' }}>
                  <td style={{ padding: '14px 12px', color: '#1a3322', fontSize: '14px', fontWeight: 600 }}>{pos.name}</td>
                  <td style={{ padding: '14px 12px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>{pos.city}</td>
                  <td style={{ padding: '14px 12px', textAlign: 'center', color: '#1890ff', fontSize: '15px', fontWeight: 700 }}>{pos.inscrits || 0}</td>
                  <td style={{ padding: '14px 12px', textAlign: 'center', color: '#00A651', fontSize: '15px', fontWeight: 700 }}>{pos.gagnants || 0}</td>
                  <td style={{ padding: '14px 12px', textAlign: 'center', color: '#faad14', fontSize: '15px', fontWeight: 700 }}>{pos.bouteilles || 0}</td>
                  <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                    <span style={{ padding: '5px 14px', borderRadius: '20px', background: 'rgba(0,192,96,0.15)', color: '#00A651', fontWeight: 700, fontSize: '13px', border: `1px solid rgba(0,192,96,0.4)` }}>
                      {pos.performance || 0}%
                    </span>
                  </td>
                </tr>
              ))}
              {!stats?.by_pos?.length && (
                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'rgba(0,0,0,0.35)', fontSize: '14px' }}>
                  <i className="fas fa-chart-bar fa-2x d-block mb-2" style={{ opacity: 0.3, color: G }}></i>
                  Aucune donnée disponible
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const CommercialPressureTable = ({ hourlyData }) => {
  const [selectedPDV, setSelectedPDV] = useState('all');
  const [selectedHour, setSelectedHour] = useState('all');
  const [hoveredCell, setHoveredCell] = useState(null);

  const getAllDates = () => {
    if (!hourlyData) return [];
    if (Array.isArray(hourlyData.all_dates) && hourlyData.all_dates.length > 0)
      return [...hourlyData.all_dates].sort().reverse();
    if (!hourlyData.by_pdv) return [];
    const dates = new Set();
    hourlyData.by_pdv.forEach(pdv => Object.keys(pdv.dates || {}).forEach(d => dates.add(d)));
    return Array.from(dates).sort().reverse();
  };

  const getAllPDVs = () => {
    if (!hourlyData || !hourlyData.by_pdv) return [];
    return hourlyData.by_pdv.map(pdv => ({ id: pdv.pdv_id, name: pdv.pdv_name }));
  };

  const getFilteredData = () => {
    if (!hourlyData || !hourlyData.by_pdv) return { dates: [], pdvs: [] };
    let pdvs = hourlyData.by_pdv;
    if (selectedPDV !== 'all') pdvs = pdvs.filter(p => p.pdv_id === parseInt(selectedPDV));
    const hourFilter = h => selectedHour === 'all' ? true : h === parseInt(selectedHour);
    const dates = getAllDates();
    return {
      dates,
      pdvs: pdvs.map(pdv => {
        const data = { id: pdv.pdv_id, name: pdv.pdv_name, totals: {}, hourlyBreakdown: {} };
        dates.forEach(date => {
          const dateData = (pdv.dates || {})[date];
          if (dateData && dateData.hourly) {
            let total = 0;
            dateData.hourly.forEach(h => { if (hourFilter(h.hour)) total += h.bottles || 0; });
            data.totals[date] = total;
            data.hourlyBreakdown[date] = dateData.hourly.filter(h => hourFilter(h.hour));
          } else {
            data.totals[date] = 0;
            data.hourlyBreakdown[date] = [];
          }
        });
        data.total = Object.values(data.totals).reduce((s, v) => s + v, 0);
        return data;
      })
    };
  };

  const getHourlyBreakdown = (pdv, date) => {
    const breakdown = (pdv.hourlyBreakdown[date] || []).filter(h => h.bottles > 0);
    if (!breakdown.length) return 'Aucune vente';
    return breakdown.map(h => `${String(h.hour).padStart(2, '0')}h : ${h.bottles}`).join(' | ');
  };

  const filteredData = getFilteredData();
  const allDates = filteredData.dates;
  const columnTotals = {};
  allDates.forEach(date => {
    columnTotals[date] = filteredData.pdvs.reduce((s, p) => s + (p.totals[date] || 0), 0);
  });
  const grandTotal = Object.values(columnTotals).reduce((s, v) => s + v, 0);

  return (
    <div style={{ background: CARD, borderRadius: '16px', padding: '24px', border: `1px solid ${BORDER}`, boxShadow: SHADOW, marginBottom: '28px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ width: '40px', height: '40px', background: 'rgba(19,194,194,0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fa-solid fa-chart-line" style={{ color: '#13c2c2', fontSize: '18px' }}></i>
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1a3322' }}>Pression Commerciale</h3>
          <p style={{ margin: 0, fontSize: '12px', color: G, fontWeight: 600 }}>Bouteilles vendues par date et par point de vente</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ color: 'rgba(0,0,0,0.55)', fontSize: '12px', fontWeight: 600 }}>Affichage :</label>
          <select value={selectedHour} onChange={e => setSelectedHour(e.target.value)}
            style={{ padding: '8px 12px', background: '#f8faf9', border: `1px solid rgba(0,192,96,0.3)`, borderRadius: '8px', color: '#1a3322', fontSize: '13px', cursor: 'pointer' }}>
            <option value="all">Toutes les heures</option>
            {Array.from({ length: 14 }, (_, i) => i + 8).map(h => (
              <option key={h} value={h}>{h}h</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ color: 'rgba(0,0,0,0.55)', fontSize: '12px', fontWeight: 600 }}>Point de vente :</label>
          <select value={selectedPDV} onChange={e => setSelectedPDV(e.target.value)}
            style={{ padding: '8px 12px', background: '#f8faf9', border: `1px solid rgba(0,192,96,0.3)`, borderRadius: '8px', color: '#1a3322', fontSize: '13px', minWidth: '160px', cursor: 'pointer' }}>
            <option value="all">Tous les points de vente</option>
            {getAllPDVs().map(pdv => <option key={pdv.id} value={pdv.id}>{pdv.name}</option>)}
          </select>
        </div>
      </div>

      {allDates.length === 0 || filteredData.pdvs.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(0,0,0,0.35)', fontSize: '14px' }}>
          <i className="fas fa-bottle-water fa-2x d-block mb-2" style={{ opacity: 0.3, color: G }}></i>
          Aucune donnée disponible
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: '8px', border: `1px solid rgba(0,192,96,0.2)` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ padding: '14px 12px', textAlign: 'left', background: `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)`, color: '#fff', position: 'sticky', left: 0, zIndex: 10, fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Point de vente
                </th>
                {allDates.map(date => {
                  const d = new Date(date);
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <th key={date} style={{ padding: '14px 8px', textAlign: 'center', background: isToday ? 'linear-gradient(135deg, #52c41a 0%, #00A651 100%)' : `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)`, color: '#fff', minWidth: '60px', fontWeight: 600, fontSize: '13px', border: isToday ? '2px solid #ffd700' : 'none', boxShadow: isToday ? '0 0 10px rgba(255,215,0,0.5)' : 'none' }}>
                      <div style={{ fontSize: '15px', fontWeight: 600 }}>{String(d.getDate()).padStart(2,'0')}/{String(d.getMonth()+1).padStart(2,'0')}</div>
                      <div style={{ fontSize: '11px', fontWeight: 400, opacity: 0.9 }}>{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                    </th>
                  );
                })}
                <th style={{ padding: '14px 12px', textAlign: 'center', background: `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)`, color: '#fff', fontWeight: 700, fontSize: '13px' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.pdvs.map(pdv => (
                <tr key={pdv.id} style={{ borderBottom: `1px solid rgba(0,192,96,0.1)` }}>
                  <td style={{ padding: '14px 12px', color: '#1a3322', position: 'sticky', left: 0, background: '#fff', fontWeight: 500, fontSize: '13px', borderRight: `1px solid rgba(0,192,96,0.15)` }}>
                    {pdv.name}
                  </td>
                  {allDates.map(date => {
                    const value = pdv.totals[date] || 0;
                    const cellKey = `${pdv.id}|${date}`;
                    return (
                      <td key={date} onMouseEnter={() => setHoveredCell(cellKey)} onMouseLeave={() => setHoveredCell(null)}
                        style={{ padding: '12px 8px', textAlign: 'center', cursor: value > 0 ? 'help' : 'default' }}>
                        <span style={{ display: 'inline-block', padding: value > 0 ? '4px 8px' : '4px', borderRadius: '4px', background: value > 0 ? `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)` : 'transparent', color: value > 0 ? '#fff' : 'rgba(0,0,0,0.25)', fontWeight: value > 0 ? 700 : 400, fontSize: '14px', boxShadow: value > 0 ? `0 2px 8px rgba(0,192,96,0.35)` : 'none', transform: hoveredCell === cellKey ? 'scale(1.1)' : 'scale(1)', transition: 'all 0.2s' }}>
                          {value}
                        </span>
                      </td>
                    );
                  })}
                  <td style={{ padding: '14px 12px', textAlign: 'center', color: '#faad14', fontWeight: 700, fontSize: '15px', textShadow: '0 0 10px rgba(255,215,0,0.4)' }}>
                    {pdv.total}
                  </td>
                </tr>
              ))}
              <tr style={{ background: `rgba(0,192,96,0.08)`, borderTop: `2px solid rgba(0,192,96,0.3)` }}>
                <td style={{ padding: '14px 12px', color: '#faad14', fontWeight: 700, position: 'sticky', left: 0, background: `rgba(0,192,96,0.08)`, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px', borderRight: `1px solid rgba(0,192,96,0.2)` }}>Total</td>
                {allDates.map(date => {
                  const total = columnTotals[date] || 0;
                  const isToday = new Date(date).toDateString() === new Date().toDateString();
                  return (
                    <td key={date} style={{ padding: '14px 8px', textAlign: 'center', color: isToday ? '#faad14' : G, fontWeight: 700, fontSize: '15px' }}>{total}</td>
                  );
                })}
                <td style={{ padding: '14px 12px', textAlign: 'center', color: '#faad14', fontWeight: 800, fontSize: '16px', textShadow: '0 0 15px rgba(255,215,0,0.5)' }}>{grandTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {hoveredCell && (() => {
        const [pdvId, dateStr] = hoveredCell.split('|');
        const pdv = filteredData.pdvs.find(p => p.id === parseInt(pdvId));
        if (!pdv) return null;
        const d = new Date(dateStr);
        return (
          <div style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', background: 'rgba(10,31,12,0.98)', border: `1px solid rgba(0,192,96,0.5)`, borderRadius: '8px', padding: '16px', zIndex: 1000, minWidth: '200px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
            <div style={{ color: G, fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>{pdv.name}</div>
            <div style={{ color: '#ffd700', fontWeight: 500, marginBottom: '4px', fontSize: '12px' }}>{String(d.getDate()).padStart(2,'0')}/{String(d.getMonth()+1).padStart(2,'0')} - {d.toLocaleDateString('fr-FR', { weekday: 'long' })}</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', lineHeight: 1.6 }}>{getHourlyBreakdown(pdv, dateStr)}</div>
          </div>
        );
      })()}
    </div>
  );
};

const KPICard = ({ title, value, icon, color }) => (
  <div style={{ background: CARD, borderRadius: '16px', padding: '22px', border: `1px solid ${BORDER}`, boxShadow: SHADOW }}>
    <div style={{ width: '54px', height: '54px', background: color + '22', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px', boxShadow: `0 4px 12px ${color}22` }}>
      <i className={`fa-solid ${icon}`} style={{ color: color, fontSize: '26px' }}></i>
    </div>
    <div style={{ fontSize: '32px', fontWeight: 800, color: '#1a3322', marginBottom: '6px', lineHeight: 1 }}>{value.toLocaleString()}</div>
    <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.55)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
  </div>
);

export default Dashboard;
